import { Plugin, PluginKey } from '@milkdown/kit/prose/state';
import type { Transaction } from '@milkdown/kit/prose/state';
import { linkSchema } from '@milkdown/kit/preset/commonmark';
import { $prose } from '@milkdown/kit/utils';

/**
 * Metadata keys for link transactions
 */
export const TENTATIVE_LINK_META = 'tentativeLinkMark';
export const CONFIRM_LINK_META = 'confirmLinkMark';
/** Set by the tooltip when the user dismisses without confirming; plugin then cleans up empty links. */
export const CLEANUP_TENTATIVE_LINK_META = 'cleanupTentativeLinkMark';

/**
 * Tracks ranges of tentative (empty href) link marks that need cleanup
 */
interface TentativeLinkRange {
  from: number;
  to: number;
}

interface TentativeLinkPluginState {
  tentativeLinks: TentativeLinkRange[];
}

const tentativeLinkPluginKey = new PluginKey<TentativeLinkPluginState>('tentativeLinkPlugin');

/**
 * Creates a ProseMirror plugin that automatically cleans up tentative (empty) link marks.
 *
 * When a transaction creates a link with metadata { tentativeLinkMark: true }, the plugin
 * tracks that range. On the next transaction (unless it has { confirmLinkMark: true }),
 * the tentative link is automatically removed by appending removal steps to the transaction.
 *
 * This prevents empty link marks from lingering when users click away without entering a URL.
 */
export const tentativeLinkPlugin = $prose((ctx) => {
  const linkType = linkSchema.type(ctx);

  return new Plugin<TentativeLinkPluginState>({
    key: tentativeLinkPluginKey,

    state: {
      init(): TentativeLinkPluginState {
        return { tentativeLinks: [] };
      },

      apply(tr: Transaction, oldState: TentativeLinkPluginState): TentativeLinkPluginState {
        // Map old-doc positions to new-doc positions after this transaction's steps
        const mapping = tr.mapping;

        // Map existing tentative link ranges through document changes (same pattern as grammarCheckDecorationPlugin)
        let tentativeLinks = oldState.tentativeLinks
          .map((link) => ({
            from: mapping.map(link.from),
            to: mapping.map(link.to),
          }))
          .filter((link) => link.from < link.to); // drop ranges invalidated by deletion

        // If this transaction confirms a link, clear tentative links fully inside that range
        const confirmMeta = tr.getMeta(CONFIRM_LINK_META);
        if (confirmMeta) {
          const confirmFrom = mapping.map(confirmMeta.from);
          const confirmTo = mapping.map(confirmMeta.to);
          tentativeLinks = tentativeLinks.filter((link) => !(link.from >= confirmFrom && link.to <= confirmTo));
        }

        // If this transaction creates a tentative link, track it (meta positions are in old doc)
        const tentativeMeta = tr.getMeta(TENTATIVE_LINK_META);
        if (tentativeMeta) {
          const from = mapping.map(tentativeMeta.from);
          const to = mapping.map(tentativeMeta.to);
          if (from < to) {
            tentativeLinks.push({ from, to });
          }
        }

        return { tentativeLinks };
      },
    },

    appendTransaction(transactions, oldState, newState) {
      // Skip if no tentative links to clean up
      const pluginState = this.getState(newState);
      if (!pluginState || pluginState.tentativeLinks.length === 0) {
        return null;
      }

      // Check if any transaction confirms or creates tentative links (don't clean up)
      const hasMetadata = transactions.some((tr) => tr.getMeta(CONFIRM_LINK_META) || tr.getMeta(TENTATIVE_LINK_META));
      const requestCleanup = transactions.some((tr) => tr.getMeta(CLEANUP_TENTATIVE_LINK_META));
      const anyDocChanged = transactions.some((tr) => tr.docChanged);
      if (hasMetadata) {
        return null;
      }
      // Run cleanup when the document was edited, or when the tooltip explicitly requested it (user dismissed).
      if (!anyDocChanged && !requestCleanup) {
        return null;
      }

      // Clean up all tentative links that still have empty hrefs
      const tr = newState.tr;
      let modified = false;

      for (const link of pluginState.tentativeLinks) {
        // Check if link still has empty href before removing
        let hasEmptyHref = false;
        newState.doc.nodesBetween(link.from, link.to, (node) => {
          const mark = node.marks.find((m) => m.type === linkType);
          if (mark && (!mark.attrs.href || mark.attrs.href === '')) {
            hasEmptyHref = true;
          }
        });

        if (hasEmptyHref) {
          tr.removeMark(link.from, link.to, linkType);
          modified = true;
        }
      }

      return modified ? tr : null;
    },
  });
});

export { tentativeLinkPluginKey };
